/**
 * 家校选人：学生标签字典 + 名单映射（单选/多选公共能力）。
 * 创建于 2026-10-29
 * 最后更新：2026-11-02 — 按学生 Id 批量取标签，避免 GetStudentDismissalTagQuery 分页 200 截断导致计数不准
 */
import { getDismissalTags, getStudentDismissalTagsByStudentIds } from '../api/dismissal'
import { toDeptId } from './deptCascaderProps'

/**
 * 从 el-tree 节点向上解析校区 Id。
 * @param {object} node el-tree 节点
 * @returns {string}
 */
export function resolveCampusIdFromTreeNode(node) {
  let n = node
  while (n && n.data) {
    const t = String((n.data.DeptOrgType != null ? n.data.DeptOrgType : n.data.deptOrgType) || '').toLowerCase()
    if (t === 'campus') {
      const id = n.data.value != null ? n.data.value : n.data.Id
      return id != null ? String(id) : ''
    }
    n = n.parent
  }
  return ''
}

/**
 * 规范化部门组织类型（用于标签查询 scope）。
 * @param {string} deptOrgType
 * @returns {string}
 */
export function normalizeStudentTagScopeType(deptOrgType) {
  let scopeType = String(deptOrgType || 'class').toLowerCase()
  const allowed = ['class', 'grade', 'campus', 'school', 'affiliateschool']
  if (!allowed.includes(scopeType)) scopeType = 'class'
  return scopeType
}

/**
 * 规范化标签简要项。
 * @param {object} t
 * @returns {{ id: *, tagName: string, categoryName: string, applyToDismissal: boolean, isEnabled: boolean }|null}
 */
function normalizeTagBrief(t) {
  if (!t) return null
  const id = t.id != null ? t.id : t.Id
  if (id == null || id === '') return null
  return {
    id,
    tagName: t.tagName != null ? t.tagName : (t.TagName || ''),
    categoryName: t.categoryName != null ? t.categoryName : (t.CategoryName || ''),
    applyToDismissal: t.applyToDismissal != null ? !!t.applyToDismissal : !!t.ApplyToDismissal,
    isEnabled: t.isEnabled != null ? t.isEnabled !== false : t.IsEnabled !== false,
  }
}

/**
 * 拉取启用中的学生标签字典，并按当前名单学生 Id 批量映射 studentTags。
 * @param {object} opts
 * @param {string|number} [opts.deptId] 当前选中部门（兼容旧调用；批量接口不再依赖）
 * @param {string|number} [opts.campusId] 校区
 * @param {string} [opts.deptOrgType] 部门类型（兼容旧调用）
 * @param {Array} opts.rows 人员行（需含 id 字段）
 * @param {string} [opts.rowIdKey='value'] 行上学生 Id 字段名
 * @returns {Promise<{ tagOptions: Array, rows: Array }>}
 */
export function fetchAndMapStudentTags(opts) {
  const rows = (opts && opts.rows) || []
  const rowIdKey = (opts && opts.rowIdKey) || 'value'
  if (!rows.length) {
    return Promise.resolve({ tagOptions: [], rows })
  }

  const campusId = toDeptId(opts && opts.campusId) || ''
  const studentIds = []
  const seen = {}
  rows.forEach((s) => {
    const raw = s && s[rowIdKey]
    if (raw == null || raw === '') return
    const key = String(raw)
    if (seen[key]) return
    seen[key] = true
    // 雪花 Id 保持字符串传入，后端再解析；同时兼容数字。最后更新：2026-11-02
    const asNum = Number(raw)
    studentIds.push(Number.isSafeInteger(asNum) && asNum > 0 ? asNum : raw)
  })

  if (!studentIds.length) {
    return Promise.resolve({ tagOptions: [], rows })
  }

  const tagDictPromise = campusId
    ? getDismissalTags({ campusId }).then((res) => {
        const raw = (res.data && res.data.response) || []
        return (raw || []).map((t) => ({
          id: t.Id != null ? t.Id : t.id,
          categoryName: t.CategoryName != null ? t.CategoryName : t.categoryName,
          tagName: t.TagName != null ? t.TagName : t.tagName,
          applyToDismissal: t.ApplyToDismissal != null ? t.ApplyToDismissal : t.applyToDismissal,
          isEnabled: t.IsEnabled != null ? t.IsEnabled : t.isEnabled,
        })).filter((t) => t.id && t.isEnabled !== false)
      }).catch(() => [])
    : Promise.resolve([])

  const body = { studentIds }
  if (campusId) body.campusId = Number(campusId) || campusId

  const stuTagPromise = getStudentDismissalTagsByStudentIds(body).then((res) => {
    return (res.data && res.data.response) || []
  }).catch(() => [])

  return Promise.all([tagDictPromise, stuTagPromise]).then(([tagOptions, assignments]) => {
    let options = tagOptions || []
    const tagMeta = {}
    options.forEach((t) => { tagMeta[String(t.id)] = t })

    const byStudent = {}
    ;(assignments || []).forEach((a) => {
      const sid = String(a.studentId != null ? a.studentId : (a.StudentId || ''))
      if (!sid) return
      const rawTags = a.tags || a.Tags || []
      byStudent[sid] = rawTags
        .map((t) => {
          const brief = normalizeTagBrief(t)
          if (!brief) return null
          const meta = tagMeta[String(brief.id)]
          if (meta) {
            return {
              id: meta.id,
              tagName: meta.tagName,
              categoryName: meta.categoryName,
              applyToDismissal: !!meta.applyToDismissal,
            }
          }
          if (brief.isEnabled === false) return null
          return {
            id: brief.id,
            tagName: brief.tagName,
            categoryName: brief.categoryName,
            applyToDismissal: !!brief.applyToDismissal,
          }
        })
        .filter(Boolean)
    })

    // 无校区字典时，用实际绑定标签拼出筛选项，避免下拉空白。最后更新：2026-11-02
    if (!options.length) {
      const fallback = {}
      Object.keys(byStudent).forEach((sid) => {
        ;(byStudent[sid] || []).forEach((t) => {
          if (!t || t.id == null) return
          fallback[String(t.id)] = {
            id: t.id,
            tagName: t.tagName,
            categoryName: t.categoryName,
            applyToDismissal: !!t.applyToDismissal,
            isEnabled: true,
          }
        })
      })
      options = Object.keys(fallback).map((k) => fallback[k])
    }

    const mapped = rows.map((s) => ({
      ...s,
      studentTags: byStudent[String(s[rowIdKey])] || [],
    }))
    return { tagOptions: options, rows: mapped }
  })
}

/**
 * 学生标签下拉展示文案（非放学前缀）。
 * @param {object} tag
 * @returns {string}
 */
export function formatStudentTagOptionLabel(tag) {
  if (!tag) return ''
  const prefix = tag.applyToDismissal ? '' : '【非放学】'
  return `${prefix}${tag.categoryName || ''}：${tag.tagName || ''}`
}
